<script lang="ts">
  import { CheckIcon } from "lucide-svelte";
  import { route } from "@mateothegreat/svelte5-router";
  import { updateMeta, injectJsonLd } from "../../seo/meta";
  import { ROUTE_SEO } from "../../seo/routes";
  import { ORGANIZATION_SCHEMA, SERVICES_SCHEMA, SERVICES_FAQ_SCHEMA, combineSchemas, getBreadcrumbSchema } from "../../seo/schemas";
  updateMeta(ROUTE_SEO["/services"]);
  injectJsonLd(combineSchemas(
    ORGANIZATION_SCHEMA,
    SERVICES_SCHEMA,
    SERVICES_FAQ_SCHEMA,
    getBreadcrumbSchema([
      { name: "Home", url: "https://0xfire.com/" },
      { name: "Services", url: "https://0xfire.com/services" },
    ]),
  ));
</script>

<div class="min-h-screen bg-black text-gray-300 selection:bg-orange-900 selection:text-white">
  <!-- Hero -->
  <div class="text-center py-20 px-4">
    <div class="badge badge-outline rounded-none uppercase tracking-widest font-mono text-xs text-orange-500 border-orange-500/50 p-3 mb-6">
      Services
    </div>
    <h1 class="text-4xl md:text-5xl font-black text-white tracking-tight mb-4">
      Build & Scale Your <span class="text-orange-500">Trading Systems</span>
    </h1>
    <p class="text-xl text-gray-500 max-w-2xl mx-auto">
      From a free intro call to a fully managed strategy. Pick your level.
    </p>
  </div>

  <!-- 3 Tiered Cards -->
  <div class="max-w-5xl mx-auto px-4 pb-24">
    <div class="grid md:grid-cols-3 gap-6">

      <!-- Tier 1 — Free Consultation -->
      <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-8 flex flex-col">
        <div class="h-1 bg-emerald-500 -mx-8 -mt-8 mb-6 rounded-t-sm"></div>
        <p class="text-[10px] font-mono text-emerald-500 uppercase tracking-[0.3em] mb-3">Starter</p>
        <h3 class="text-2xl font-bold text-white mb-1">Free Consultation</h3>
        <div class="mb-4">
          <span class="text-3xl font-black text-white">Free</span>
        </div>
        <p class="text-sm text-gray-500 mb-6">
          15-minute intro call. We understand your goals and recommend the right path forward.
        </p>
        <ul class="space-y-3 mb-8 flex-1">
          <li class="flex items-start gap-2 text-sm">
            <CheckIcon class="size-4 text-emerald-500 mt-0.5 shrink-0" />
            <span>Understand your trading goals</span>
          </li>
          <li class="flex items-start gap-2 text-sm">
            <CheckIcon class="size-4 text-emerald-500 mt-0.5 shrink-0" />
            <span>Technical feasibility assessment</span>
          </li>
          <li class="flex items-start gap-2 text-sm">
            <CheckIcon class="size-4 text-emerald-500 mt-0.5 shrink-0" />
            <span>Personalized recommendation</span>
          </li>
        </ul>
        <p class="text-[10px] text-gray-700 text-center mb-2 font-mono">No commitment</p>
        <a
          href="https://calendly.com/0xconstantin/30min"
          target="_blank"
          class="btn btn-block bg-emerald-600 hover:bg-emerald-500 text-white border-none rounded-sm uppercase tracking-widest font-bold transition-colors"
        >
          Book Free Call
        </a>
      </div>

      <!-- Tier 2 — Build Your First Bot -->
      <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-8 flex flex-col">
        <div class="h-1 bg-blue-500 -mx-8 -mt-8 mb-6 rounded-t-sm"></div>
        <p class="text-[10px] font-mono text-blue-500 uppercase tracking-[0.3em] mb-3">Mentorship</p>
        <h3 class="text-2xl font-bold text-white mb-1">Build Your First Bot</h3>
        <div class="mb-4">
          <span class="text-3xl font-black text-white">$499</span>
          <span class="text-gray-500">/month</span>
        </div>
        <p class="text-sm text-gray-500 mb-6">
          Weekly 1-on-1 sessions where I teach you to build your first trading bot from scratch.
        </p>
        <ul class="space-y-3 mb-8 flex-1">
          <li class="flex items-start gap-2 text-sm">
            <CheckIcon class="size-4 text-blue-500 mt-0.5 shrink-0" />
            <span>1 hour/week live tutoring calls</span>
          </li>
          <li class="flex items-start gap-2 text-sm">
            <CheckIcon class="size-4 text-blue-500 mt-0.5 shrink-0" />
            <span>Hands-on bot building project</span>
          </li>
          <li class="flex items-start gap-2 text-sm">
            <CheckIcon class="size-4 text-blue-500 mt-0.5 shrink-0" />
            <span>Code review & best practices</span>
          </li>
          <li class="flex items-start gap-2 text-sm">
            <CheckIcon class="size-4 text-blue-500 mt-0.5 shrink-0" />
            <span>Community access</span>
          </li>
        </ul>
        <a
          href="https://calendly.com/0xconstantin/30min"
          target="_blank"
          class="btn btn-block bg-blue-600 hover:bg-blue-500 text-white border-none rounded-sm uppercase tracking-widest font-bold transition-colors"
        >
          Get Started
        </a>
      </div>

      <!-- Tier 3 — Scale Your Strategy -->
      <div class="bg-gray-950/40 border border-orange-500/30 rounded-sm p-8 flex flex-col relative">
        <div class="h-1 bg-orange-500 -mx-8 -mt-8 mb-6 rounded-t-sm"></div>
        <span class="absolute top-3 right-4 text-[9px] font-mono uppercase tracking-widest text-orange-500 border border-orange-500/30 rounded-none px-2 py-0.5">
          Full Service
        </span>
        <p class="text-[10px] font-mono text-orange-500 uppercase tracking-[0.3em] mb-3">Partnership</p>
        <h3 class="text-2xl font-bold text-white mb-1">Scale Your Strategy</h3>
        <div class="mb-4">
          <span class="text-3xl font-black text-white">$1,199</span>
          <span class="text-gray-500">/month</span>
        </div>
        <p class="text-sm text-gray-500 mb-6">
          I implement your strategy, backtest it, and set it up for you to run live. Full-service retainer.
        </p>
        <ul class="space-y-3 mb-8 flex-1">
          <li class="flex items-start gap-2 text-sm">
            <CheckIcon class="size-4 text-orange-500 mt-0.5 shrink-0" />
            <span>Strategy implementation from your idea</span>
          </li>
          <li class="flex items-start gap-2 text-sm">
            <CheckIcon class="size-4 text-orange-500 mt-0.5 shrink-0" />
            <span>Backtesting on historical data</span>
          </li>
          <li class="flex items-start gap-2 text-sm">
            <CheckIcon class="size-4 text-orange-500 mt-0.5 shrink-0" />
            <span>Live trading setup & deployment</span>
          </li>
          <li class="flex items-start gap-2 text-sm">
            <CheckIcon class="size-4 text-orange-500 mt-0.5 shrink-0" />
            <span>Source code & ongoing support</span>
          </li>
          <li class="flex items-start gap-2 text-sm">
            <CheckIcon class="size-4 text-orange-500 mt-0.5 shrink-0" />
            <span>Hosting & maintenance</span>
          </li>
        </ul>
        <a
          href="https://calendly.com/0xconstantin/30min"
          target="_blank"
          class="btn btn-block bg-orange-600 hover:bg-orange-500 text-white border-none rounded-sm uppercase tracking-widest font-bold transition-colors"
        >
          Let's Build
        </a>
      </div>
    </div>

    <!-- FAQ Section -->
    <div class="max-w-3xl mx-auto pt-16">
      <h2 class="text-2xl font-bold text-white mb-8 tracking-tight">Frequently Asked Questions</h2>
      <div class="space-y-6">
        <div>
          <h3 class="text-sm font-bold text-white mb-1">What programming languages do you use for trading bots?</h3>
          <p class="text-sm text-gray-500 leading-relaxed">Most of our bots are built in TypeScript (Node.js) for rapid development and Rust for latency-critical execution. We also work with Python for backtesting and data analysis.</p>
        </div>
        <div>
          <h3 class="text-sm font-bold text-white mb-1">How long does it take to build a trading bot?</h3>
          <p class="text-sm text-gray-500 leading-relaxed">A basic bot can be built in 2-4 weeks with the mentorship program. A production-grade strategy with backtesting and deployment typically takes 4-8 weeks in the full-service tier.</p>
        </div>
        <div>
          <h3 class="text-sm font-bold text-white mb-1">What chains and exchanges do you build bots for?</h3>
          <p class="text-sm text-gray-500 leading-relaxed">We build bots across Solana DEXs (Raydium, PumpFun, Meteora), Ethereum, Binance Chain, Hyperliquid, and prediction markets (Polymarket, Kalshi).</p>
        </div>
        <div>
          <h3 class="text-sm font-bold text-white mb-1">Do I need coding experience for the mentorship program?</h3>
          <p class="text-sm text-gray-500 leading-relaxed">Basic programming knowledge helps, but you don't need to be an expert. The mentorship is designed to take you from beginner to building your first bot step by step.</p>
        </div>
        <div>
          <h3 class="text-sm font-bold text-white mb-1">Do you guarantee profits?</h3>
          <p class="text-sm text-gray-500 leading-relaxed">No. We build infrastructure and help develop strategies, but trading is inherently risky. We focus on building robust systems and rigorous backtesting to maximize your edge.</p>
        </div>
      </div>
    </div>

    <!-- Related Links -->
    <div class="max-w-3xl mx-auto pt-12">
      <p class="text-xs font-mono text-gray-600 uppercase tracking-widest mb-4">Related</p>
      <div class="flex flex-wrap gap-3">
        <a use:route href="/products/sailfish" class="text-sm text-gray-500 hover:text-white transition-colors">Sailfish API</a>
        <span class="text-gray-700">&middot;</span>
        <a use:route href="/tools" class="text-sm text-gray-500 hover:text-white transition-colors">Free Tools</a>
        <span class="text-gray-700">&middot;</span>
        <a use:route href="/workshops/dont-get-wrecked" class="text-sm text-gray-500 hover:text-white transition-colors">Crypto Workshop</a>
      </div>
    </div>
  </div>
</div>
